/**
 * M3 — protected routes have an empty `body` in the public SQL dump
 * (stripped post-build by `scripts/partition-protected-content.mjs`).
 * The Worker fetched the real body from the private R2 bucket and stashed
 * it on `event.context.protectedContent` in `server/middleware/auth.ts`
 * before the page's setup ran. This helper reads it for the page composable
 * to splice onto a fetched row so `<ContentRenderer>` gets the AST it expects.
 *
 * Must be called from sync setup context (before any await). On Cloudflare
 * Workers, Nuxt's async context is not preserved across awaits (no
 * AsyncLocalStorage compat flag), so `useNuxtApp()` throws "instance
 * unavailable" if called from inside a useAsyncData async handler. Capture
 * the body once in the outer composable scope, then close over it.
 */
export function getProtectedBody(): unknown | null {
  if (!import.meta.server) return null
  try {
    return useNuxtApp().ssrContext?.event?.context.protectedContent?.body ?? null
  } catch {
    return null
  }
}

// Client-side inflight dedup: a single docs route renders the page,
// the docs layout, and BreadcrumbNav — each invokes `usePageContent` with
// the same key. `useAsyncData`'s natural dedup is bypassed here because
// the composable's custom `getCachedData` returns `undefined` for rows with
// empty bodies (forcing a re-fetch when the partition step cleared the
// body in the cached payload). Without this map, three concurrent calls
// fire three identical `/api/protected-content` requests.
const _protectedBodyInflight = new Map<string, Promise<unknown>>()

/**
 * Client-side fallback when SSR didn't run (SPA navigation). Calls the
 * BFF endpoint that mirrors the SSR middleware's auth gate + R2 read.
 * Returns the parsed body AST or `null` on any failure (401/403/missing).
 */
export async function fetchProtectedBodyClient(path: string): Promise<unknown | null> {
  const cached = _protectedBodyInflight.get(path)
  if (cached) return cached
  const promise = (async () => {
    try {
      const res = await $fetch<{ body: unknown }>("/api/protected-content", {
        query: { path },
      })
      return res?.body ?? null
    } catch {
      // 401/403 means the session expired between auth.global accepting the
      // route and this fetch landing — render the empty row and let the next
      // nav re-trigger the OIDC flow. Other failures (missing object, 5xx)
      // are also handled by failing closed to "no body".
      return null
    }
  })()
  _protectedBodyInflight.set(path, promise)
  // Keep the cached promise after it resolves so the page/layout/breadcrumb
  // can all read the same answer during the same nav. Cleared on the next
  // hard reload or session-level lifecycle event.
  return promise
}
